import React from 'react';

import CaretIcon from '@hero/ui-kit/icons/utility/CaretIcon';
import { getFormattedDate, parseToDate } from '../../../../utils/date';
import { PastSevenDaysEvent } from '../../types';
import * as Style from './style.module.scss';

const getEventTitle = (type: PastSevenDaysEvent['event_type'], count: number): string => {
    switch (type) {
        case 'DEVICE_OFFLINE':
            return `Device went offline ${count} time${count > 1 ? 's' : ''} in the last 7 days`;
        case 'OPEN_DOOR':
            return `Safety key used ${count} time${count > 1 ? 's' : ''} in the last 7 days`;
        default:
            return 'Unknown event';
    }
};

const getEventAlertType = (
    type: PastSevenDaysEvent['event_type'],
    count: number
): 'error' | 'warning' | 'info' => {
    switch (type) {
        case 'DEVICE_OFFLINE':
            return count > 15 ? 'error' : count > 5 ? 'warning' : 'info';
        case 'OPEN_DOOR':
            return count > 1 ? 'error' : 'info';
        default:
            return 'info';
    }
};

const DeviceEvent: React.FC<PastSevenDaysEvent> = ({ event_type, event_count, timestamps }) => {
    const [isExpanded, setIsExpanded] = React.useState(false);

    if (!event_type || !event_count) {
        return null;
    }

    const expandable = event_type === 'OPEN_DOOR' && timestamps && timestamps.length > 0;

    const title = getEventTitle(event_type, event_count);
    const alertType = getEventAlertType(event_type, event_count);

    const toggleExpanded = () => setIsExpanded((prev) => !prev);

    const interactiveProps = expandable
        ? {
              role: 'button',
              tabIndex: 0,
              'aria-expanded': isExpanded,
              onClick: toggleExpanded,
              onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      toggleExpanded();
                  }
              }
          }
        : {};

    return (
        <div
            className={`${Style.deviceEventCard} ${Style[alertType]} ${
                expandable ? Style.expandable : ''
            }`}
            {...interactiveProps}
        >
            <div className={Style.deviceEventHeader}>
                <div className={Style.deviceEventTitle}>
                    {expandable && (
                        <CaretIcon
                            size="small"
                            direction={isExpanded ? 'down' : 'forward'}
                            className={Style.deviceEventCaretIcon}
                        />
                    )}
                    <strong>{title}</strong>
                </div>
            </div>

            {timestamps && isExpanded && (
                <div className={Style.deviceEventExpandedContent}>
                    <ul style={{ margin: 0, padding: '0 0 0 16px' }}>
                        {timestamps.map((item) => (
                            <li key={item} className="monospace" style={{ marginBottom: '6px' }}>
                                <span>
                                    {getFormattedDate(parseToDate(item), 'MMM dd, yyyy (HH:mm)')}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default DeviceEvent;
